import d3 = require("d3");
import moment = require("moment");
import React = require("react");
import ReactFauxDOM = require("react-faux-dom");
import {Duration} from "moment";
import {Types} from "../../../types/Types";
import {Granularity, GranularityType} from "../Toolbar";
import "./Timeline.scss";

interface TimelineProps {
    key: number;
    enabled: boolean;
    totalPeriod: Types.Period;
    selectedPeriod: Types.Period;
    size: Types.Size;
    margin: Types.Margin;
    selectedGranularity: Granularity;
    dateTimeFormat: string;
    handleBrush: (period: Types.Period) => any;
    handleBrushEnd: (period: Types.Period) => any;
    onBrush?: (event: any) => any;
    onBrushStart?: () => any;
    onBrushEnd?: () => any;
}

export class Timeline extends React.Component<TimelineProps, {}> {
    private static logEnabled: boolean = false;

    private static numberOfInstances = 0;

    public node;

    private instanceNumber: number;

    constructor(props) {
        super(props);
        this.createBrush = this.createBrush.bind(this);
        this.node = React.createRef();
        this.instanceNumber = Timeline.numberOfInstances++;
    }

    public componentDidMount() {
        this.createBrush();
    }

    public componentDidUpdate() {
        this.createBrush();
    }

    public render() {
        const div = new ReactFauxDOM.Element("div");
        div.setAttribute("class", "svg-faux-dom timeline-instance-" + this.instanceNumber);
        const svg = d3.select(div).select("svg");
        if (svg.node() == null) {
            d3.select(div).append("svg")
                .attr("width", this.props.size.width)
                .attr("height", this.props.size.height)
                .attr("ref", this.node);
        }
        return div.toReact();
    }

    private onBrushEnd(timeScale: any, event) {
        if (event.sourceEvent && event.sourceEvent.type === "brush") {
            return;
        }
        const brushNode = d3.select(this.node.current).select(".brush").node() as SVGGElement;
        if (brushNode == null) {
            return;
        }
        let brushSelection = d3.brushSelection(brushNode);
        if (!this.checkBrushSelection(brushSelection, timeScale)) {
            this.log("Brush Selection empty or not valid");
            if (brushSelection == null) {
                return;
            }
            brushSelection[0] = timeScale(this.props.selectedPeriod.start);
            brushSelection[1] = timeScale(this.props.selectedPeriod.end);
            return;
        }

        this.props.handleBrushEnd({
            start: moment(timeScale.invert(brushSelection[0]).getTime()),
            end: moment(timeScale.invert(brushSelection[1]).getTime()),
        });
        brushSelection = d3.brushSelection(brushNode);

        this.updateBrushTooltip(brushSelection, timeScale, event.sourceEvent);
    }

    private updateBrushTooltip(brushSelection: d3.BrushSelection, timeScale: any, sourceEvent: any) {
        // update brush tooltips
        d3.select(this.node.current)
            .select(".brush")
            .selectAll(".brush-tooltip")
            .attr("transform", (d: any): string => {
                if (d.key === "start") {
                    return "translate(" + (brushSelection[0] as number + 7) + ",10)";
                } else {
                    return "translate(" + (brushSelection[1] as number - 7) + ",25)";
                }
            })
            .text((d: any): string => {
                if (d.key === "start") {
                    return this.props.selectedPeriod.start.format(this.props.dateTimeFormat);
                } else {
                    return this.props.selectedPeriod.end.format(this.props.dateTimeFormat);
                }
            });
    }

    private onBrush(timeScale: any, event) {
        if (event.sourceEvent && event.sourceEvent.type === "brush") {
            return;
        }
        const brushNode = d3.select(this.node.current).select(".brush").node() as SVGGElement;
        if (brushNode == null) {
            return;
        }
        const brushSelection = d3.brushSelection(brushNode);
        if (!this.checkBrushSelection(brushSelection, timeScale)) {
            this.log("Brush Selection empty or to small");
            return;
        }
        this.props.handleBrush({
            start: moment(timeScale.invert(brushSelection[0]).getTime()),
            end: moment(timeScale.invert(brushSelection[1]).getTime()),
        });
        this.updateBrushTooltip(brushSelection, timeScale, event.sourceEvent);
    }

    private createBrush() {
        if (!this.props.enabled) {
            return;
        }
        const node = this.node.current;
        const timeScale = d3.scaleTime().domain([this.props.totalPeriod.start, this.props.totalPeriod.end])
            .range([0, this.props.size.width]);

        const timeAxis = d3.axisBottom(timeScale);

        // create the brush axis group
        d3.select(node)
            .selectAll("g.brushaxis")
            .data([0])
            .enter()
            .append("g")
            .attr("class", "brushaxis")
            .attr("transform", "translate(0,25)");
        // draw the brush axis
        d3.select(node)
            .select("g.brushaxis")
            .call((selection: d3.Selection<SVGSVGElement, any, any, any>) => timeAxis(selection));

        // attach data to the brush
        d3.select(node)
            .selectAll("g.brush")
            .data([0])
            .enter()
            .append("g")
            .attr("class", "brush");

        const brush = d3.brushX()
            .extent([[0, 0], [this.props.size.width, this.props.size.height]])
            .handleSize(10);

        d3.select(node)
            .select("g.brush")
            .call(brush)
            .call((group: d3.Selection<SVGGElement, unknown, any, any>,
                   selection: any,
                   event?: Event) => brush.move(group, selection, event),
                [this.props.selectedPeriod.start, this.props.selectedPeriod.end].map(timeScale));

        brush.on("brush", (event) => this.onBrush(timeScale, event));
        // brush.on("end", () => this.onBrushEnd(timeScale));
        brush.on("end", (event) => {
            const minBrushSize = this.getBrushMinSize(timeScale);
            if (event.selection == null || event.selection[1] - event.selection[0] < minBrushSize) {
                d3.select(node)
                    .select("g.brush")
                    .call(brush)
                    .call((group: d3.Selection<SVGGElement, unknown, any, any>,
                           selection: any,
                           event?: Event) => brush.move(group, selection, event),
                        [this.props.selectedPeriod.start, this.props.selectedPeriod.end].map(timeScale));
            } else {
                this.onBrushEnd(timeScale, event);
            }
        });
        this.drawBrushAnchors(timeScale);
        this.drawBrushTooltips(timeScale);

        $(node)
            .find("[pointer-events]")
            .removeAttr("pointer-events");
    }

    private getBrushMinSize(timeScale): number {
        switch (this.props.selectedGranularity.key) {
            case GranularityType.YEAR:
                return timeScale(moment(timeScale.invert(0)).add(1, "year"));
            case GranularityType.QUARTER:
                return timeScale(moment(timeScale.invert(0)).add(1, "quarter"));
            case GranularityType.MONTH:
                return timeScale(moment(timeScale.invert(0)).add(1, "month"));
            case GranularityType.WEEK:
                return timeScale(moment(timeScale.invert(0)).add(1, "week"));
            case GranularityType.DAY:
                return timeScale(moment(timeScale.invert(0)).add(1, "day"));
            case GranularityType.HOUR:
                return timeScale(moment(timeScale.invert(0)).add(1, "hour"));
        }
        return timeScale(moment(timeScale.invert(0)).add(1, "day"));
    }

    private getBrushSelectionMinDuration(): Duration {
        switch (this.props.selectedGranularity.key) {
            case GranularityType.YEAR:
                return moment.duration(1, "year");
            case GranularityType.QUARTER:
                return moment.duration(1, "quarter");
            case GranularityType.MONTH:
                return moment.duration(1, "month");
            case GranularityType.WEEK:
                return moment.duration(1, "week");
            case GranularityType.DAY:
                return moment.duration(1, "day");
            case GranularityType.HOUR:
                return moment.duration(1, "hour");
        }
        return moment.duration(1, "day");
    }

    private drawBrushAnchors(timeScale: any) {
        const brushAnchorsData = timeScale.ticks(this.props.selectedGranularity.timeInterval);
        const node = this.node.current;

        d3.select(node)
            .select(".brush")
            .selectAll(".brush-anchors")
            .data(["anchors"])
            .enter()
            .append("g")
            .attr("class", "brush-anchors");
        const brushAnchorsGroup = d3.select(".timeline-instance-" + this.instanceNumber + " .brush-anchors");

        brushAnchorsGroup
            .selectAll(".brush-anchor")
            .remove();
        brushAnchorsGroup
            .selectAll(".brush-anchor")
            .data(brushAnchorsData)
            .enter()
            .append("rect")
            .attr("class", (d) => "brush-anchor")
            .attr("height", this.props.size.height)
            .attr("width", 1);
        brushAnchorsGroup
            .selectAll(".brush-anchor")
            .data(timeScale.ticks(this.props.selectedGranularity.timeInterval))
            .attr("transform", (d) => "translate(" + timeScale(d) + ",0)");
    }

    private drawBrushTooltips(timeScale: any) {
        const node = this.node.current;
        const brushNode = d3.select(this.node.current).select(".brush").node() as SVGGElement;
        const brushSelection = d3.brushSelection(brushNode);

        const brushTooltipsData = [{date: this.props.selectedPeriod.start, key: "start"}, {
            date: this.props.selectedPeriod.end,
            key: "end",
        }];

        const x1: any = d3.select(node)
            .select(".brush")
            .selectAll(".brush-tooltips")
            .data(["tooltips"]);

        const x2 = x1.enter()
            .append("g")
            .attr("class", "brush-tooltips");
        // brushTooltipGroup = d3.select(".brush-tooltips");

        const brushTooltipGroup = x2.merge(x1);

        brushTooltipGroup.selectAll(".brush-tooltip")
            .data(brushTooltipsData)
            .enter()
            .append("text")
            .attr("class", (d) => "brush-tooltip brush-tooltip" + "__" + d.key);

        const brushTooltipsText = brushTooltipGroup
            .selectAll(".brush-tooltip");

        brushTooltipsText
            .text((d) => (d.date != null ? d.date.format(this.props.dateTimeFormat) : ""))
            .attr("transform", (d: any): string => {
                if (d.key === "start") {
                    return "translate(" + (timeScale(this.props.selectedPeriod.start.toDate()) + 7) + ",10)";
                } else {
                    return "translate(" + (timeScale(this.props.selectedPeriod.end.toDate()) - 7) + ",25)";
                }
            });

        let brushTooltipsBackground = brushTooltipGroup
            .selectAll(".brush-tooltip-bkgrd");

        const tooltipBkGrdData = [];
        brushTooltipsText.nodes().forEach(
            (text) => {
                const textData: any = d3.select(text).datum();
                tooltipBkGrdData.push({key: textData.key, bbox: text.getBBox()});
            },
        );

        brushTooltipsBackground.data(tooltipBkGrdData)
            .enter()
            .insert("rect", ".brush-tooltip__start")
            .attr("class", (d) => "brush-tooltip-bkgrd");
        brushTooltipsBackground = brushTooltipGroup
            .selectAll(".brush-tooltip-bkgrd");

        brushTooltipsBackground
            .attr("width", (d: any) => d.bbox.width)
            .attr("height", (d: any) => d.bbox.height)
            .attr("transform", (d: any): string => {
                if (d.key === "start") {
                    return "translate(" + (timeScale(this.props.selectedPeriod.start.toDate()) + 7) + ",0)";
                } else {
                    return "translate(" +
                        (timeScale(this.props.selectedPeriod.end.toDate()) - 7 - d.bbox.width) + ", 15)";
                }
            });
    }

    /**
     * check if the given brush selection is empty or smaller then min granularity unit day for the given timeScale
     */
    private checkBrushSelection = (selection: d3.BrushSelection, timeScale: any): boolean => {
        if (selection == null ||
            ((selection[1] as number) - (selection[0] as number) === 0) ||
            (timeScale.invert(selection[1]).getTime() - timeScale.invert(selection[0]).getTime())
            < this.getBrushSelectionMinDuration().asMilliseconds()) {
            this.log("Brush Selection empty or to small");
            return false;
        }
        return true;
    }

    private log(message: any): void {
        if (Timeline.logEnabled) {
            console.log(message);
        }
    }
}
